import React, { useMemo, useState } from 'react';

/**
 * Date-range selector with the standard report presets plus a custom range.
 * Emits { from, to } as ISO strings via onChange.
 *
 * Presets: today | yesterday | 7d | 30d | month | prevMonth | fy | custom
 *   `fy` = current Indian financial year (Apr 1 → today).
 */
export const RANGE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 Days' },
  { key: '30d', label: 'Last 30 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'prevMonth', label: 'Previous Month' },
  { key: 'fy', label: 'Current FY (Apr → Now)' },
  { key: 'custom', label: 'Custom Range' },
];

// All report-date presets are evaluated as IST (Asia/Kolkata) calendar days regardless of the
// browser's/device's local timezone — the clinic's data is IST, so "Today" must mean the IST
// calendar day or it silently drops same-day rows whenever the client clock isn't on IST
// (e.g. a server-side render, a misconfigured OS timezone, or daylight-saving elsewhere).
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

/** Y/M/D of `d`'s instant as seen on the IST wall clock. */
function istParts(d) {
  const shifted = new Date(d.getTime() + IST_OFFSET_MS);
  return { y: shifted.getUTCFullYear(), m: shifted.getUTCMonth(), date: shifted.getUTCDate() };
}
/** Real UTC instant for 00:00:00.000 IST on the given IST calendar date. */
const istDayStart = (y, m, date) => new Date(Date.UTC(y, m, date, 0, 0, 0, 0) - IST_OFFSET_MS);
/** Real UTC instant for 23:59:59.999 IST on the given IST calendar date. */
const istDayEnd = (y, m, date) => new Date(Date.UTC(y, m, date, 23, 59, 59, 999) - IST_OFFSET_MS);
/** "YYYY-MM-DD" → {y, m (0-indexed), date}, parsed as plain calendar parts (no timezone guessing). */
function parseDateOnlyParts(s) {
  const [y, m, date] = s.split('-').map(Number);
  return { y, m: m - 1, date };
}

/** Indian FY runs Apr 1 → Mar 31. Returns Apr 1 (IST) of the FY that contains `now`. */
function fyStartIst(now) {
  const { y, m } = istParts(now);
  // Months are 0-indexed; April = 3. Before April we're still in the prior FY.
  const fyYear = m >= 3 ? y : y - 1;
  return istDayStart(fyYear, 3, 1);
}

export function rangeForPreset(key, customFrom, customTo) {
  const now = new Date();
  const today = istParts(now);
  switch (key) {
    case 'today':
      return { from: istDayStart(today.y, today.m, today.date).toISOString(), to: istDayEnd(today.y, today.m, today.date).toISOString() };
    case 'yesterday': {
      const y = istDayStart(today.y, today.m, today.date - 1);
      const yParts = istParts(y);
      return { from: y.toISOString(), to: istDayEnd(yParts.y, yParts.m, yParts.date).toISOString() };
    }
    case '7d':
      return { from: istDayStart(today.y, today.m, today.date - 6).toISOString(), to: istDayEnd(today.y, today.m, today.date).toISOString() };
    case '30d':
      return { from: istDayStart(today.y, today.m, today.date - 29).toISOString(), to: istDayEnd(today.y, today.m, today.date).toISOString() };
    case 'month':
      return { from: istDayStart(today.y, today.m, 1).toISOString(), to: istDayEnd(today.y, today.m, today.date).toISOString() };
    case 'prevMonth': {
      const f = istDayStart(today.y, today.m - 1, 1);
      const t = istDayStart(today.y, today.m, 1);
      return { from: f.toISOString(), to: t.toISOString() };
    }
    case 'fy':
      return {
        from: fyStartIst(now).toISOString(),
        to: istDayEnd(today.y, today.m, today.date).toISOString(),
      };
    case 'custom': {
      // Picking only ONE of the two dates means "just that single day" — mirror it onto the
      // other side instead of leaving it open-ended, so a single click shows a specific day's
      // data without also having to fill in a matching end date.
      const fromStr = customFrom || customTo;
      const toStr = customTo || customFrom;
      const f = fromStr ? parseDateOnlyParts(fromStr) : null;
      const t = toStr ? parseDateOnlyParts(toStr) : null;
      return {
        from: f ? istDayStart(f.y, f.m, f.date).toISOString() : null,
        to: t ? istDayEnd(t.y, t.m, t.date).toISOString() : null,
      };
    }
    default:
      return { from: istDayStart(today.y, today.m, 1).toISOString(), to: istDayEnd(today.y, today.m, today.date).toISOString() };
  }
}

export default function DateRangePicker({ preset, onPresetChange, customFrom, customTo, onCustomChange, size = 'md' }) {
  const isCustom = preset === 'custom';
  return (
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select
        className="form-control"
        style={{ width: size === 'sm' ? 150 : 170 }}
        value={preset}
        onChange={(e) => onPresetChange(e.target.value)}
      >
        {RANGE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>
      {isCustom && (
        <>
          <input
            className="form-control" style={{ width: 150 }} type="date" title="From"
            value={customFrom || ''} onChange={(e) => onCustomChange({ from: e.target.value, to: customTo })}
          />
          <span className="td-muted">→</span>
          <input
            className="form-control" style={{ width: 150 }} type="date" title="To"
            value={customTo || ''} onChange={(e) => onCustomChange({ from: customFrom, to: e.target.value })}
          />
        </>
      )}
    </div>
  );
}

/** Hook: manages preset + custom dates and returns the resolved { from, to } range. */
export function useDateRange(initialPreset = '30d') {
  const [preset, setPreset] = useState(initialPreset);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const range = useMemo(() => rangeForPreset(preset, customFrom, customTo), [preset, customFrom, customTo]);
  const onCustomChange = ({ from, to }) => { setCustomFrom(from); setCustomTo(to); };
  return { preset, setPreset, customFrom, customTo, onCustomChange, range };
}
